import { useState, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useProductStore } from '@/store/product';
import styles from './nav.module.scss';

/**
 * Resource entries the nav search can surface alongside products. Each maps a
 * set of keywords/close variations to a single result that links to the
 * matching section of `/resources` (currently one page, so all share a path).
 */
const RESOURCE_RESULTS = [
  {
    title: 'Installation & Care',
    keywords: [
      'installation',
      'install',
      'installation guide',
      'care',
      'floor care',
      'cleaning',
      'maintenance',
    ],
    path: '/resources',
  },
  {
    title: 'Molding',
    keywords: [
      'mold',
      'molding',
      'moldings',
      'moulding',
      'trim',
      'trims',
      'stair nose',
      'reducer',
      't-mold',
    ],
    path: '/resources',
  },
  {
    title: 'Warranty',
    keywords: ['warranty', 'guarantee'],
    path: '/resources',
  },
];

const NavSearchBar = ({ onNavigate }) => {
  const navigate = useNavigate();
  const collections = useProductStore(s => s.collections);
  const [searchInput, setSearchInput] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const inputRef = useRef(null);

  const normalize = value =>
    String(value ?? '')
      .trim()
      .toLowerCase()
      .replace(/\s+/g, ' ');

  const searchResults = useMemo(() => {
    if (!isOpen) return [];

    const normalizedInput = normalize(searchInput);
    if (!normalizedInput) {
      const allProducts = [];
      for (const collection of collections ?? []) {
        for (const product of collection.products ?? []) {
          allProducts.push({
            type: 'product',
            product_name: product.product_name,
            collection_name: collection.collection_name,
            collectionKey: collection.routeKey,
            productKey: product.routeKey,
          });
        }
      }
      return allProducts.slice(0, 6);
    }

    const resourceResults = RESOURCE_RESULTS.filter(resource =>
      resource.keywords.some(keyword => {
        const normalizedKeyword = normalize(keyword);
        return (
          normalizedKeyword.includes(normalizedInput) || normalizedInput.includes(normalizedKeyword)
        );
      })
    ).map(resource => ({ type: 'resource', title: resource.title, path: resource.path }));

    const productResults = [];
    for (const collection of collections ?? []) {
      for (const product of collection.products ?? []) {
        const productName = normalize(product.product_name);
        if (productName.includes(normalizedInput)) {
          productResults.push({
            type: 'product',
            product_name: product.product_name,
            collection_name: collection.collection_name,
            collectionKey: collection.routeKey,
            productKey: product.routeKey,
          });
        }
        if (productResults.length >= 6) break;
      }
      if (productResults.length >= 6) break;
    }

    return [...resourceResults, ...productResults].slice(0, 6);
  }, [searchInput, isOpen, collections]);

  const handleInputChange = e => {
    setSearchInput(e.target.value);
    setSelectedIndex(-1);
  };

  const handleInputFocus = () => {
    setIsOpen(true);
    setSelectedIndex(-1);
  };

  const handleInputBlur = () => {
    setTimeout(() => setIsOpen(false), 150);
  };

  const handleSelectResult = result => {
    if (result.type === 'resource') {
      navigate(result.path);
    } else {
      navigate(`/products/${result.collectionKey}/${result.productKey}`);
    }
    setSearchInput('');
    setIsOpen(false);
    setSelectedIndex(-1);
    onNavigate?.();
  };

  const handleKeyDown = e => {
    if (!isOpen) {
      if (e.key === 'Enter' || e.key === ' ') {
        setIsOpen(true);
      }
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev => (prev < searchResults.length - 1 ? prev + 1 : prev));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev => (prev > 0 ? prev - 1 : -1));
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0 && searchResults[selectedIndex]) {
          handleSelectResult(searchResults[selectedIndex]);
        }
        break;
      case 'Escape':
        setIsOpen(false);
        setSelectedIndex(-1);
        break;
      default:
        break;
    }
  };

  return (
    <div className={styles['nav-search-container']}>
      <div className={styles['nav-search-wrapper']}>
        <svg
          className={styles['nav-search-icon']}
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          aria-hidden="true"
        >
          <circle cx="11" cy="11" r="8" />
          <path d="m21 21-4.35-4.35" />
        </svg>
        <input
          ref={inputRef}
          type="text"
          className={styles['nav-search-input']}
          placeholder="Search"
          value={searchInput}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          onBlur={handleInputBlur}
          onKeyDown={handleKeyDown}
          aria-label="Search products or resources"
          aria-autocomplete="list"
          aria-controls="nav-search-results"
          aria-expanded={isOpen}
        />
      </div>

      {isOpen && searchResults.length > 0 && (
        <div className={styles['nav-search-dropdown']} id="nav-search-results" role="listbox">
          {searchResults.map((result, idx) => {
            const isResource = result.type === 'resource';
            const key = isResource
              ? `resource-${result.title}`
              : `product-${result.collectionKey}-${result.productKey}`;
            return (
              <button
                key={key}
                className={`${styles['nav-search-item']} ${
                  idx === selectedIndex ? styles['selected'] : ''
                }`}
                onClick={() => handleSelectResult(result)}
                onMouseEnter={() => setSelectedIndex(idx)}
                role="option"
                aria-selected={idx === selectedIndex}
              >
                <div className={styles['nav-result-name']}>
                  {isResource ? result.title : result.product_name}
                </div>
                <div className={styles['nav-result-collection']}>
                  {isResource ? 'Resources' : result.collection_name}
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default NavSearchBar;
